// @flow strict

import * as React from 'react'
import { useEffect, useState } from 'react'
import { type PropsWithChildren } from '../../../utils/typeUtils'
import { NavigationItem, type NavigationItemProps } from './NavigationItem'
import {
  FullNavigation,
  MinimalNavigation,
  SvgDefs,
} from './NavigationComponents'

export type NavStyles = { [k: string]: string }
const NavigationContainer = ({
  styles,
  navigationListStyles,
  navLinks,
  callToAction,
  activeContext,
  children,
  logoHref,
}: PropsWithChildren<
  {
    styles: NavStyles,
    navigationListStyles: NavStyles,
    navLinks: NavigationItemProps[],
    activeContext: 'personal' | 'business',
    callToAction?: React.Node,
    logoHref?: string,
  },
  React.Element<typeof NavigationItem>[],
>) => {
  const navigationStyles = styles
  const [isNavigationVisible, setIsNavigationVisible] = useState(false)
  const toggleNavigationVisibility = () =>
    setIsNavigationVisible(!isNavigationVisible)

  useEffect(() => {
    // Stop the page from being scrollable
    // when the navigation is visible on mobile devices
    const html = document.querySelector('html')
    if (!html) return

    const touchMoveListener = (evt: Event): void => evt.preventDefault()

    if (isNavigationVisible) {
      html.addEventListener('touchmove', touchMoveListener)
      html.classList.add(navigationStyles.noScroll)
    } else {
      html.removeEventListener('touchmove', touchMoveListener)
      html.classList.remove(navigationStyles.noScroll)
    }

    return () => {
      html.removeEventListener('touchmove', touchMoveListener)
    }
  }, [isNavigationVisible, navigationStyles.noScroll])

  return (
    <>
      <SvgDefs
        navigationStyles={navigationStyles}
        hasDropdowns={navLinks.some(
          navLink =>
            navLink.dropdownItems != null && navLink.dropdownItems.length > 0,
        )}
      />
      <MinimalNavigation
        onClick={toggleNavigationVisibility}
        navigationStyles={navigationStyles}
        logoHref={logoHref}
      />
      <FullNavigation
        isNavigationVisible={isNavigationVisible}
        setIsNavigationVisible={toggleNavigationVisibility}
        navigationStyles={navigationStyles}
        navigationListStyles={navigationListStyles}
        navLinks={navLinks}
        activeContext={activeContext}
        callToAction={callToAction}
        logoHref={logoHref}
      >
        {children}
      </FullNavigation>
      {isNavigationVisible && <div className={navigationStyles.overlay} />}
    </>
  )
}

export default NavigationContainer
